/**
 * Phase 4 static capability audit — derived from Django source read-only review.
 * Used by /classic-site/diagnostics/api-capabilities (GET-only diagnostics).
 *
 * Classification values:
 * supported | partial | read-only | hybrid | blocked | unsafe | unknown-staging
 */

export const CAPABILITY_CLASS = {
  SUPPORTED: "supported",
  PARTIAL: "partial",
  READ_ONLY: "read-only",
  HYBRID: "hybrid",
  BLOCKED: "blocked",
  UNSAFE: "unsafe",
  UNKNOWN: "unknown-staging",
};

export const CAPABILITY_LABELS = {
  [CAPABILITY_CLASS.SUPPORTED]: "Supported now",
  [CAPABILITY_CLASS.PARTIAL]: "Partially supported",
  [CAPABILITY_CLASS.READ_ONLY]: "Read-only only",
  [CAPABILITY_CLASS.HYBRID]: "Hybrid (Django redirect)",
  [CAPABILITY_CLASS.BLOCKED]: "Blocked — missing API",
  [CAPABILITY_CLASS.UNSAFE]: "Unsafe without backend change",
  [CAPABILITY_CLASS.UNKNOWN]: "Unknown — staging test required",
};

/** @type {Array<{domain: string, capability: string, django: string, api: string, react: string, classification: string, notes: string}>} */
export const MEMBER_LISTING_CAPABILITIES = [
  {
    domain: "listings",
    capability: "List current member's listings",
    django: "Member.directoryentry_set; account_overview count",
    api: "GET /api/hoodie/listings/ — public_listings only; no owner filter",
    react: "Blocked panel (Phase 3)",
    classification: CAPABILITY_CLASS.BLOCKED,
    notes: "No ?mine filter. ?favorites= is not ownership.",
  },
  {
    domain: "listings",
    capability: "Read one owned listing",
    django: "PartnerEntryDetailView — all member listings",
    api: "GET /api/hoodie/listings/{id}/ — only if enabled in public queryset",
    react: "getListingById (public)",
    classification: CAPABILITY_CLASS.PARTIAL,
    notes: "Disabled/unverified listings may 404. No ownership proof in response.",
  },
  {
    domain: "listings",
    capability: "Create listing",
    django: "PartnerEntryCreateView — sets administrator, entry_type, enabled via Stripe rules",
    api: "POST /api/hoodie/listings/ — DjangoModelPermissionsOrAnonReadOnly; no view logic for enabled/administrator",
    react: "None",
    classification: CAPABILITY_CLASS.UNSAFE,
    notes: "Serializer missing recommended_by, administrator, entry_type, enabled. IsListingOwnerOrReadOnly exists but NOT wired.",
  },
  {
    domain: "listings",
    capability: "Edit owned listing",
    django: "PartnerEntryEditView — owner via UpdateView queryset",
    api: "PATCH /api/hoodie/listings/{id}/ — no object-level ownership on ViewSet",
    react: "patchListingById (admin-tools only)",
    classification: CAPABILITY_CLASS.UNKNOWN,
    notes: "Depends on Django model perms + whether object is in public queryset. Staging required.",
  },
  {
    domain: "listings",
    capability: "Delete owned listing",
    django: "PartnerEntryDeleteView — dispatch checks administrator",
    api: "DELETE /api/hoodie/listings/{id}/",
    react: "None",
    classification: CAPABILITY_CLASS.UNKNOWN,
    notes: "No ownership mixin on ViewSet. Staging required.",
  },
  {
    domain: "listings",
    capability: "Upload/update listing graphic",
    django: "PartnerDirectoryEntryForm.graphic",
    api: "PATCH multipart — graphic in serializer",
    react: "None",
    classification: CAPABILITY_CLASS.UNKNOWN,
    notes: "Field present; multipart + ownership unverified in staging.",
  },
  {
    domain: "listings",
    capability: "Review listing before publish",
    django: "listing_review template redirect",
    api: "None",
    react: "None",
    classification: CAPABILITY_CLASS.HYBRID,
    notes: "Client-side review step possible later; publish rules need enabled logic.",
  },
  {
    domain: "listings",
    capability: "Bulk enable/disable listings",
    django: "update_active_listings POST",
    api: "None",
    react: "None",
    classification: CAPABILITY_CLASS.HYBRID,
    notes: "Subscription quantity rules in view only.",
  },
  {
    domain: "listings",
    capability: "Listing enabled/active state",
    django: "View sets enabled from subscription; member toggles via bulk form",
    api: "enabled not in serializer",
    react: "None",
    classification: CAPABILITY_CLASS.UNSAFE,
    notes: "Cannot replicate enable logic via API without backend change.",
  },
  {
    domain: "listings",
    capability: "Listing tags",
    django: "PartnerDirectoryEntryForm — max 6 tags",
    api: "tags in serializer; GET /api/hoodie/tags/",
    react: "getTags",
    classification: CAPABILITY_CLASS.PARTIAL,
    notes: "Read supported; write with listing PATCH unknown.",
  },
  {
    domain: "listings",
    capability: "recommended_by field",
    django: "Required on form",
    api: "Not in DirectoryEntrySerializer",
    react: "None",
    classification: CAPABILITY_CLASS.UNSAFE,
    notes: "Strict parity blocked without serializer change.",
  },
  {
    domain: "listings",
    capability: "administrator ownership",
    django: "Set on create to request.user.member",
    api: "Not in serializer; not settable via API",
    react: "None",
    classification: CAPABILITY_CLASS.UNSAFE,
    notes: "Backend view logic only.",
  },
  {
    domain: "listings",
    capability: "subscription_active behavior",
    django: "System field on model",
    api: "Not in serializer",
    react: "None",
    classification: CAPABILITY_CLASS.BLOCKED,
    notes: "Admin-only; do not expose to member UI.",
  },
];

export const MEMBER_EVENT_CAPABILITIES = [
  {
    domain: "events",
    capability: "List events for owned listing",
    django: "EntryEventListing — all events for entry_id, no LoginRequired",
    api: "GET /api/hoodie/events/ — active, future, enabled entry only",
    react: "getEvents (public)",
    classification: CAPABILITY_CLASS.BLOCKED,
    notes: "No entry_id filter on API list. Past/inactive excluded.",
  },
  {
    domain: "events",
    capability: "Read one event",
    django: "EntryEventUpdate detail",
    api: "GET /api/hoodie/events/{id}/ — if in filtered queryset",
    react: "None",
    classification: CAPABILITY_CLASS.PARTIAL,
    notes: "Inactive/past events may not be retrievable.",
  },
  {
    domain: "events",
    capability: "Create event",
    django: "EntryEventCreate — ownership check on create",
    api: "POST /api/hoodie/events/ — entry not in writable serializer fields",
    react: "None",
    classification: CAPABILITY_CLASS.UNSAFE,
    notes: "entry is read-only nested on serializer. No ownership on ViewSet.",
  },
  {
    domain: "events",
    capability: "Edit event",
    django: "EntryEventUpdate — no explicit auth on view",
    api: "PATCH /api/hoodie/events/{id}/",
    react: "None",
    classification: CAPABILITY_CLASS.UNKNOWN,
    notes: "active not in event serializer. Staging required.",
  },
  {
    domain: "events",
    capability: "Delete event",
    django: "EntryEventDelete — no explicit auth",
    api: "DELETE /api/hoodie/events/{id}/",
    react: "None",
    classification: CAPABILITY_CLASS.UNKNOWN,
    notes: "Staging required.",
  },
  {
    domain: "events",
    capability: "Inactive/past events for owner",
    django: "Full queryset per listing",
    api: "Filtered out of queryset",
    react: "None",
    classification: CAPABILITY_CLASS.BLOCKED,
    notes: "Requires backend editor queryset.",
  },
];

export const MEMBER_PROMO_CAPABILITIES = [
  {
    domain: "promos",
    capability: "List promos for owned listing",
    django: "EntryPromoListing — all promos for entry",
    api: "GET /api/hoodie/promos/ — active, enabled entry only",
    react: "getPromos (public)",
    classification: CAPABILITY_CLASS.BLOCKED,
    notes: "No entry_id filter. Inactive promos excluded.",
  },
  {
    domain: "promos",
    capability: "Read one promo",
    django: "EntryPromoUpdate detail",
    api: "GET /api/hoodie/promos/{id}/",
    react: "None",
    classification: CAPABILITY_CLASS.PARTIAL,
    notes: "Inactive promos may 404.",
  },
  {
    domain: "promos",
    capability: "Create promo",
    django: "EntryPromoCreate — ownership check",
    api: "POST /api/hoodie/promos/",
    react: "None",
    classification: CAPABILITY_CLASS.UNKNOWN,
    notes: "entry read-only on serializer. Staging required.",
  },
  {
    domain: "promos",
    capability: "Edit promo",
    django: "EntryPromoUpdate",
    api: "PATCH /api/hoodie/promos/{id}/ — active in serializer",
    react: "None",
    classification: CAPABILITY_CLASS.UNKNOWN,
    notes: "Staging required.",
  },
  {
    domain: "promos",
    capability: "Delete promo",
    django: "EntryPromoDelete",
    api: "DELETE /api/hoodie/promos/{id}/",
    react: "None",
    classification: CAPABILITY_CLASS.UNKNOWN,
    notes: "Staging required.",
  },
  {
    domain: "promos",
    capability: "Inactive promos for owner",
    django: "Full listing queryset",
    api: "active=True filter only",
    react: "None",
    classification: CAPABILITY_CLASS.BLOCKED,
    notes: "Requires backend editor queryset.",
  },
];

export const BILLING_CAPABILITIES = [
  { capability: "Account overview", classification: CAPABILITY_CLASS.HYBRID, django: "/member/accounts/profile/", api: "None" },
  { capability: "Subscription status", classification: CAPABILITY_CLASS.HYBRID, django: "/member/accounts/subscriptions/", api: "None" },
  { capability: "Plan selection", classification: CAPABILITY_CLASS.HYBRID, django: "/member/accounts/subscriptions/choose/", api: "None" },
  { capability: "Checkout", classification: CAPABILITY_CLASS.HYBRID, django: "/member/accounts/subscriptions/change/<plan_id>/", api: "None" },
  { capability: "Payment methods", classification: CAPABILITY_CLASS.HYBRID, django: "/member/accounts/payments/*", api: "None" },
  { capability: "Coupon validate", classification: CAPABILITY_CLASS.HYBRID, django: "GET /member/ajax/coupon/validate/", api: "AJAX JSON — session+CSRF" },
  { capability: "Attach card", classification: CAPABILITY_CLASS.HYBRID, django: "POST /member/ajax/card/add/", api: "AJAX JSON — session+CSRF" },
  { capability: "Fast track coupon", classification: CAPABILITY_CLASS.HYBRID, django: "/fasttrack/<code>/", api: "Session redirect only" },
  { capability: "Attach plan to session", classification: CAPABILITY_CLASS.HYBRID, django: "/member/plan/attach/*", api: "Session redirect only" },
  { capability: "Login", classification: CAPABILITY_CLASS.HYBRID, django: "/accounts/login/", api: "hoodies/me/ for session check only" },
  { capability: "Logout", classification: CAPABILITY_CLASS.HYBRID, django: "/accounts/logout/", api: "None" },
  { capability: "Login success redirect", classification: CAPABILITY_CLASS.HYBRID, django: "/member/login/success/", api: "None" },
];

/** Safe GET endpoints for diagnostics page */
export const DIAGNOSTIC_GET_ENDPOINTS = [
  { path: "hoodies/me/", label: "Session profile", requiresSession: true },
  { path: "listings/?limit=3", label: "Public listings sample", requiresSession: false },
  { path: "events/", label: "Public events", requiresSession: false },
  { path: "tags/", label: "Tags", requiresSession: false },
];
